import _ from 'lodash';
import uuid from 'uuid/v4';
import math from '../../V4/MathHelpers';
import Attribute from '../../V4/Attribute';
import RuleSet from '../../V4/RuleSet';
import RuleFactory from '../../V4/Factory/Rule';
import WhiteListFactory from '../../V4/Factory/WhiteList';

/**
 * @summary Given any value coerce it to a number if it can be coerced to a valid Number
 * @param value A value to coerce
 * @desc MathJS will try and coerce everything to a number (null, arrays of values, booleans)
 * so do a null check before conversion and an instanceof check after
 */
function coerceNumber(value) {
  try {
    if (_.isNil(value)) {
      return undefined;
    }

    const coercedValue = math.number(value);

    return (typeof coercedValue).toLowerCase() === 'number' ? coercedValue : undefined;
  } catch (error) {
    throw Error(`Provided value ${value} cannot be coerced to a valid Number.`);
  }
}

function coerceAttributeRangesToNumbers(attributeRanges) {
  return _.map(attributeRanges, range => ({
    ...range,
    minimum: coerceNumber(range.minimum),
    maximum: coerceNumber(range.maximum),
    increment: coerceNumber(range.increment),
  }));
}

function isNotNilOrEmpty(value) {
  return !_.isNil(value) && !_.isEmpty(value);
}

function buildRangeValueConfiguration(attributeConfiguration) {
  const configuration = {};
  if (isNotNilOrEmpty(attributeConfiguration.attributeValues)) {
    configuration.attributeValues = attributeConfiguration.attributeValues;
  } else if (isNotNilOrEmpty(attributeConfiguration.values)) {
    configuration.attributeValues = attributeConfiguration.values;
  } else if (isNotNilOrEmpty(attributeConfiguration.attributeRanges)) {
    configuration.attributeRanges = coerceAttributeRangesToNumbers(attributeConfiguration.attributeRanges);
  } else if (isNotNilOrEmpty(attributeConfiguration.ranges)) {
    configuration.attributeRanges = coerceAttributeRangesToNumbers(attributeConfiguration.ranges);
  } else if (isNotNilOrEmpty(attributeConfiguration.formula)) {
    configuration.formula = attributeConfiguration.formula;
  } else {
    // Grab the attribute key off the configuration, which is 'attributeKey' for white list
    // attributes and 'attribute' for rule component attributes.
    const attributeKey = _.isNil(attributeConfiguration.attributeKey)
      ? attributeConfiguration.attribute
      : attributeConfiguration.attributeKey;

    throw new Error(`Attribute ${attributeKey} does not have any of the following properties: "attributeValues" or "values" or ` +
        `"attributeRanges" or "ranges" or "formula". ${JSON.stringify(attributeConfiguration)}`);
  }

  return configuration;
}

function convertRuleAttributeConfiguration(attributeConfiguration) {
  const configuration = {
    key: attributeConfiguration.attributeKey,
    required: false,
    class: _.isNil(attributeConfiguration.class) ? Attribute.Classes.tenant : attributeConfiguration.class.toLowerCase(),
    subclass: attributeConfiguration.subclass,
    negate: attributeConfiguration.negate,
  };

  const valueRangeConfiguration = buildRangeValueConfiguration(attributeConfiguration);
  _.assignIn(configuration, valueRangeConfiguration);

  return configuration;
}

function convertRuleResultsToComponent(v1RuleResults) {
  const ruleComponent = {
    componentAttributes: _.map(v1RuleResults, convertRuleAttributeConfiguration),
  };

  return ruleComponent;
}

function convertRulePredicateToComponent(v1RulePredicate) {
  const ruleComponent = {
    componentAttributes: [convertRuleAttributeConfiguration(v1RulePredicate)],
  };

  return ruleComponent;
}

function convertRuleListConfiguration(v1RuleConfiguration) {
  const configuration = {
    name: v1RuleConfiguration.name,
    ruleComponents: [],
  };

  const v1RulePredicates = v1RuleConfiguration.predicates;
  if (!_.isNil(v1RulePredicates) && v1RulePredicates.length > 0) {
    const rulePredicateComponents = _.map(v1RulePredicates, convertRulePredicateToComponent);

    configuration.ruleComponents.push(...rulePredicateComponents);
  }

  const v1RuleResults = v1RuleConfiguration.results;
  if (!_.isNil(v1RuleResults) && v1RuleResults.length > 0) {
    const ruleResultComponent = convertRuleResultsToComponent(v1RuleResults);

    configuration.ruleComponents.push(ruleResultComponent);
  }

  return configuration;
}

function validateV1RuleListConfiguration(v1RuleConfiguration) {
  if (_.isNil(v1RuleConfiguration)) {
    throw new Error('v1RuleConfiguration must not be null or undefined');
  }
}

function convertV1RuleToV2(v1RuleConfiguration) {
  validateV1RuleListConfiguration(v1RuleConfiguration);

  const v2Configuration = convertRuleListConfiguration(v1RuleConfiguration);
  return RuleFactory.create(v2Configuration);
}

function convertWhiteListAttributeConfiguration(attributeConfiguration) {
  const configuration = {
    key: attributeConfiguration.attributeKey,
    required: attributeConfiguration.required,
    class: _.isNil(attributeConfiguration.class) ? Attribute.Classes.tenant : attributeConfiguration.class.toLowerCase(),
    subclass: attributeConfiguration.subclass,
    derived: attributeConfiguration.derived,
    attributeValueTranslations: attributeConfiguration.attributeValueTranslations,
    includeOnOrder: attributeConfiguration.includeOnOrder,
  };

  const valueRangeConfiguration = buildRangeValueConfiguration(attributeConfiguration);
  _.assignIn(configuration, valueRangeConfiguration);

  return configuration;
}

function whiteListContainsAttributeValue(whiteList, key, value) {
  return _.some(whiteList[key].attributeValues, attributeValue => attributeValue.value === value);
}

function whiteListContainsAttribute(whiteList, key) {
  return _.some(whiteList, attribute => attribute.key.toLowerCase() === key.toLowerCase());
}

function decontainerWhiteListConfiguration(v2WhitelistConfiguration) {
  const newWhitelistAttributes = {};
  _.each(v2WhitelistConfiguration, (configuration) => {
    _.each(configuration.attributeValueTranslations, (translation) => {
      _.each(translation.attributeSelections, (selection) => {
        const key = selection.attributeKey;
        const value = selection.attributeValue;

        if (newWhitelistAttributes[key]) {
          if (!whiteListContainsAttributeValue(newWhitelistAttributes, key, value)) {
            newWhitelistAttributes[key].attributeValues.push({ value });
          }
        } else {
          newWhitelistAttributes[key] = convertWhiteListAttributeConfiguration({
            ...selection,
            includeOnOrder: configuration.includeOnOrder,
            // Including random uuid to prevent containered attributes from implying values
            values: [{ value }, { value: `${uuid()}-prevent-implying-containered-attribute` }],
          });
        }
      });
    });
  });

  _.each(newWhitelistAttributes, (value, key) => {
    if (!whiteListContainsAttribute(v2WhitelistConfiguration, key)) {
      v2WhitelistConfiguration.push(value);
    }
  });

  return v2WhitelistConfiguration;
}

function convertWhiteListConfiguration(v1WhitelistConfiguration) {
  return _.map(v1WhitelistConfiguration, attributeConfiguration => convertWhiteListAttributeConfiguration(attributeConfiguration));
}

function validateV1WhiteListConfiguration(v1WhitelistConfiguration) {
  if (_.isNil(v1WhitelistConfiguration)) {
    throw new Error('v1WhitelistConfiguration must not be null or undefined');
  }

  if (!Array.isArray(v1WhitelistConfiguration)) {
    throw new Error('v1WhitelistConfiguration must be an array');
  }
}

function convertV1WhiteListToV2(v1WhitelistConfiguration) {
  validateV1WhiteListConfiguration(v1WhitelistConfiguration);

  const v2Configuration = convertWhiteListConfiguration(v1WhitelistConfiguration);
  const decontaineredv2Configuration = decontainerWhiteListConfiguration(v2Configuration);
  return WhiteListFactory.create(decontaineredv2Configuration);
}

/**
 * Modified copy of the V4.RuleSetAdapter changed to work with white list as defined by the Rule
 * Service's v1 Rule Set resource.
 */
class RuleSetAdapter {
  static convertV1toV2(v1RuleSet) {
    const whiteList = convertV1WhiteListToV2(v1RuleSet.whiteList);
    const ruleList = _.map(v1RuleSet.rules, v1RuleConfiguration => convertV1RuleToV2(v1RuleConfiguration));

    return new RuleSet({ whiteList, rules: ruleList });
  }
}

module.exports = RuleSetAdapter;
